import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './YandexMapPicker.css';
import { loadYandexMapsApi } from './loadYandexMapsApi';

/** Moscow — default center when coordinates are empty */
const DEFAULT_LAT = 55.751244;
const DEFAULT_LON = 37.618423;

/** Value format: `latitude,longitude` (decimal degrees, comma-separated) */
export function parseGeoCoordsPair(raw) {
	if (raw == null || String(raw).trim() === '') return null;
	const parts = String(raw)
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);
	if (parts.length < 2) return null;
	const lat = parseFloat(parts[0]);
	const lon = parseFloat(parts[1]);
	if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
	return { lat, lon };
}

/**
 * Renders only after reactify is ready — keeps reactify.useDefault hook usage valid.
 * @see https://yandex.ru/maps-api/docs/js-api/common/connection/reactify.html
 */
function YandexMapInner({ reactify, mod, ymaps3, safeLon, safeLat, onMapClick }) {
	let { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker, YMapListener } = mod;

	if (!YMapListener && ymaps3.YMapListener) {
		YMapListener = reactify.entity(ymaps3.YMapListener);
	}

	const location = reactify.useDefault(
		{ center: [safeLon, safeLat], zoom: 14 },
		[safeLon, safeLat]
	);

	const markerCoords = reactify.useDefault([safeLon, safeLat], [safeLon, safeLat]);

	return (
		<div className="ymap-picker__frame">
			<YMap location={location} mode="vector">
				<YMapDefaultSchemeLayer />
				<YMapDefaultFeaturesLayer />
				{YMapListener ? <YMapListener layer="any" onClick={onMapClick} /> : null}
				<YMapMarker coordinates={markerCoords}>
					<div className="ymap-picker__pin" aria-hidden />
				</YMapMarker>
			</YMap>
		</div>
	);
}

/**
 * Single Formik field: value is `широта, долгота` (through comma). Yandex Maps 3 + reactify.
 */
function YandexMapPicker({ field, form, placeholder, meta: _meta }) {
	const [kit, setKit] = useState(null);
	const [loadError, setLoadError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const ymaps3 = await loadYandexMapsApi();
				await ymaps3.ready;
				const ymaps3React = await ymaps3.import('@yandex/ymaps3-reactify');
				const reactify = ymaps3React.reactify.bindTo(React, ReactDOM);
				const mod = reactify.module(ymaps3);
				if (!cancelled) setKit({ reactify, mod, ymaps3 });
			} catch (e) {
				if (!cancelled) setLoadError(e.message || String(e));
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const parsed = parseGeoCoordsPair(field.value);
	const safeLat = parsed ? parsed.lat : DEFAULT_LAT;
	const safeLon = parsed ? parsed.lon : DEFAULT_LON;

	const applyCoords = useCallback(
		(lonCoord, latCoord) => {
			form.setFieldValue(field.name, `${latCoord.toFixed(6)},${lonCoord.toFixed(6)}`);
		},
		[form, field.name]
	);

	const onMapClick = useCallback(
		(...args) => {
			const ev = args.length >= 2 ? args[1] : args[0];
			const c = ev?.coordinates ?? ev?.detail?.coordinates ?? args[0]?.coordinates;
			if (!c || c.length < 2) return;
			const [clickLon, clickLat] = c;
			if (!Number.isFinite(clickLon) || !Number.isFinite(clickLat)) return;
			applyCoords(clickLon, clickLat);
		},
		[applyCoords]
	);

	if (loadError) {
		return (
			<div className="ymap-picker ymap-picker--error" role="alert">
				{loadError}
			</div>
		);
	}

	if (!kit) {
		return <div className="ymap-picker ymap-picker--loading">Загрузка карты…</div>;
	}

	const { mod } = kit;
	if (!mod?.YMap) {
		return (
			<div className="ymap-picker ymap-picker--error" role="alert">
				Не удалось инициализировать компоненты карты.
			</div>
		);
	}

	return (
		<div className="ymap-picker">
			<p className="ymap-picker__hint">
				Нажмите на карту или введите координаты ниже (широта и долгота через запятую, WGS‑84).
			</p>
			<YandexMapInner
				reactify={kit.reactify}
				mod={mod}
				ymaps3={kit.ymaps3}
				safeLon={safeLon}
				safeLat={safeLat}
				onMapClick={onMapClick}
			/>
			<input
				type="text"
				className="ymap-picker__coords-input"
				id={field.name}
				name={field.name}
				value={field.value ?? ''}
				placeholder={placeholder}
				onChange={(e) => form.setFieldValue(field.name, e.target.value)}
				onBlur={field.onBlur}
				autoComplete="off"
				spellCheck={false}
			/>
		</div>
	);
}

export default YandexMapPicker;
